import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock, Loader2, CheckCircle2, AlertTriangle, CreditCard, ExternalLink } from "lucide-react";
import { getStripe } from "@/lib/stripe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currency = (cents) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PayForm = ({ paymentIntentId, amountCents, invoiceNumber }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Payment couldn't be submitted");
        setBusy(false);
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: `${window.location.origin}/payment/success?pi=${paymentIntentId}`,
        },
      });
      if (confirmError) {
        setError(confirmError.message || "Payment failed. Try a different card.");
        setBusy(false);
        return;
      }
      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        setDone(true);
      } else {
        setError("Payment is pending — we'll email you when it clears.");
      }
    } catch (err) {
      setError(err?.message || "Unexpected error");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-10" data-testid="invoice-payment-success">
        <div className="w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 size={26} />
        </div>
        <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">
          Invoice {invoiceNumber} · paid.
        </h2>
        <p className="text-[#a395c3] text-sm mt-3 max-w-sm mx-auto">
          {currency(amountCents)} confirmed. A receipt is on its way — you can close this window.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="invoice-pay-form">
      <PaymentElement options={{ layout: "tabs" }} />

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs" role="alert">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-2 text-[10px] text-white/40 uppercase tracking-[0.24em]">
        <Lock size={10} /> Secured by Stripe · card data never touches our servers
      </div>

      <button
        type="submit"
        disabled={!stripe || busy}
        data-testid="invoice-pay-submit"
        className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Processing…
          </>
        ) : (
          <>
            <CreditCard size={14} /> Pay {currency(amountCents)}
          </>
        )}
      </button>
    </form>
  );
};

export default function PayInvoicePage() {
  const { invoiceId } = useParams();
  const [state, setState] = useState({ loading: true, error: "", secret: "", pi: "", amount: 0, number: "", client: "" });
  const [stripePromise] = useState(() => getStripe());
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!invoiceId) return;
    // Guard against React StrictMode double-effect
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/payments/invoice/intent`, { invoice_id: invoiceId });
        setState({
          loading: false,
          error: "",
          secret: data.client_secret,
          pi: data.payment_intent_id,
          amount: data.amount_cents,
          number: data.invoice_number || invoiceId.slice(0, 8),
          client: data.client_name || "",
        });
      } catch (e) {
        const detail = e?.response?.data?.detail || "Couldn't load this invoice. It may have been paid already or the link expired.";
        setState((s) => ({ ...s, loading: false, error: typeof detail === "string" ? detail : "Invoice not available" }));
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, [invoiceId]);

  const stripeOptions = state.secret
    ? {
        clientSecret: state.secret,
        appearance: {
          theme: "night",
          variables: {
            colorPrimary: "#00f0ff",
            colorBackground: "#0a0510",
            colorText: "#ffffff",
            colorDanger: "#ff0055",
            fontFamily: "Inter, system-ui, sans-serif",
            borderRadius: "12px",
          },
        },
      }
    : null;

  return (
    <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center px-4 py-10">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[120px] bg-[#00f0ff]/10" />
      </div>

      <div className="relative w-full max-w-md rounded-3xl bg-[#0a0510] border border-white/10 p-6 md:p-8">
        <a href="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 hover:text-white mb-6" data-testid="invoice-pay-home">
          <ExternalLink size={11} /> SB Media Studio
        </a>

        {state.loading && (
          <div className="py-16 text-center text-white/60 text-sm">
            <Loader2 size={18} className="inline animate-spin mr-2" />
            Loading invoice…
          </div>
        )}

        {state.error && !state.loading && (
          <div className="py-12 text-center" data-testid="invoice-pay-error">
            <AlertTriangle size={26} className="text-[#ff0055] mx-auto mb-4" />
            <h2 className="font-display font-black text-xl mb-2">Can&apos;t open this invoice</h2>
            <p className="text-[#a395c3] text-sm">{state.error}</p>
          </div>
        )}

        {!state.loading && state.secret && stripePromise && (
          <>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#00f0ff] mb-2">
              Invoice {state.number}
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">
              {currency(state.amount)}
            </h1>
            {state.client && (
              <p className="text-[#a395c3] text-sm mt-2">
                Billed to <span className="text-white">{state.client}</span>
              </p>
            )}

            <div className="mt-7">
              <Elements stripe={stripePromise} options={stripeOptions}>
                <PayForm
                  paymentIntentId={state.pi}
                  amountCents={state.amount}
                  invoiceNumber={state.number}
                />
              </Elements>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
